import { Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  IconCreditCard,
  IconPigMoney,
  IconReceipt,
  IconShoppingCart,
  IconGasStation,
} from '@tabler/icons-react';
import { formatCurrency } from '../../utils/decimal';

// Hero right column: a browser-window mock of the real app, selling the product.
// Decorative (aria-hidden) so assistive tech does not announce the sample
// figures. Currency runs through formatCurrency + tabular-nums; the macOS
// traffic-light dots are decorative chrome (documented hardcode exception).
const TRAFFIC_LIGHTS = ['#FF5F57', '#FEBC2E', '#28C840'];

const LEDGER_ROWS = [
  { name: 'Checking', icon: IconCreditCard, tone: 'checking', amount: 9542 },
  { name: 'Venmo', icon: IconCreditCard, tone: 'checking', amount: 281 },
  { name: 'AA Master Card', icon: IconCreditCard, tone: 'credit', amount: -4214 },
  { name: 'Marriott Visa', icon: IconCreditCard, tone: 'credit', amount: -1012 },
  { name: 'Savings', icon: IconPigMoney, tone: 'savings', amount: 2402 },
];

const BUDGET_ROWS = [
  { name: 'Bills', icon: IconReceipt, amount: 2872 },
  { name: 'Groceries', icon: IconShoppingCart, amount: 340 },
  { name: 'Gas', icon: IconGasStation, amount: 95 },
];

const ProductPreview = () => {
  const theme = useTheme();
  const toneColor = {
    checking: theme.palette.cat.checking,
    credit: theme.palette.cat.credit,
    savings: theme.palette.cat.savings,
  };
  const num = { fontVariantNumeric: 'tabular-nums' };

  const Row = ({ name, icon: Icon, accent, amount }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 28,
          height: 28,
          borderRadius: '8px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.13),
          color: accent,
        }}
      >
        <Icon size={15} />
      </Box>
      <Box sx={{ flex: 1, fontSize: 12, fontWeight: 500, color: theme.palette.text.primary }}>{name}</Box>
      <Box
        sx={{
          ...num,
          fontWeight: 600,
          fontSize: 12,
          color: amount < 0 ? theme.palette.error.main : theme.palette.text.primary,
        }}
      >
        {formatCurrency(amount)}
      </Box>
    </Box>
  );

  const GroupCard = ({ title, count, dotColor, children }) => (
    <Box
      sx={{
        bgcolor: theme.palette.surface.raised,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        p: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dotColor }} />
        <Box sx={{ fontWeight: 600, fontSize: 12, color: theme.palette.text.secondary }}>{title}</Box>
        <Box sx={{ fontSize: 12, color: theme.palette.text.dim }}>{count}</Box>
      </Box>
      {children}
    </Box>
  );

  return (
    <Box
      aria-hidden
      sx={{
        bgcolor: theme.palette.surface.recessed,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: `0 30px 70px ${alpha(theme.palette.surface.page, 0.55)}`,
      }}
    >
      {/* Title bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: theme.palette.surface.raised,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 0.6 }}>
          {TRAFFIC_LIGHTS.map((c) => (
            <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
          ))}
        </Box>
        <Box
          sx={{
            ml: 1,
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
            fontSize: 11,
            color: theme.palette.text.dim,
            bgcolor: theme.palette.surface.page,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          app.budgetrecon.com
        </Box>
      </Box>

      {/* App header: two stat blocks */}
      <Box sx={{ display: 'flex', gap: 4, px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        {[
          { label: 'TANGIBLE', value: 1290 },
          { label: 'NET WORTH', value: 3692 },
        ].map(({ label, value }) => (
          <Box key={label}>
            <Box sx={{ fontWeight: 600, fontSize: 8, letterSpacing: '0.07em', color: theme.palette.text.dim }}>
              {label}
            </Box>
            <Box sx={{ ...num, fontWeight: 600, fontSize: 14, color: theme.palette.success.main }}>
              {formatCurrency(value)}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Group cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, alignItems: 'start', p: 2 }}>
        <GroupCard title="Ledgers" count={8} dotColor={theme.palette.interactive}>
          {LEDGER_ROWS.map((r) => (
            <Row key={r.name} name={r.name} icon={r.icon} accent={toneColor[r.tone]} amount={r.amount} />
          ))}
        </GroupCard>
        <GroupCard title="Budgets" count={32} dotColor={theme.palette.cat.budgets}>
          {BUDGET_ROWS.map((r) => (
            <Row key={r.name} name={r.name} icon={r.icon} accent={theme.palette.cat.budgets} amount={r.amount} />
          ))}
        </GroupCard>
      </Box>
    </Box>
  );
};

export default ProductPreview;
